import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionTitle } from "@/components/AppTable";
import { toast } from "sonner";
import { Trash, Plus, ChatCircle, PaperPlaneTilt, CheckSquare, Square, ArrowClockwise } from "@phosphor-icons/react";
import { scoreColor, formatDate } from "@/lib/format";
import EmailComposer from "@/components/EmailComposer";
import BulkEmailComposer from "@/components/BulkEmailComposer";

const STATUSES = ["New", "Contacted", "Interested", "Negotiating", "Integrated", "Not Interested", "Invalid"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [colPages, setColPages] = useState({});
  const [dragOver, setDragOver] = useState(null);
  const [dragging, setDragging] = useState(null);
  const PAGE = 8;

  const load = () => api.get("/leads").then(r => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const loadNotes = (id) => api.get(`/leads/${id}/notes`).then(r => setNotes(r.data));
  const loadEmails = (id) => api.get(`/leads/${id}/emails`).then(r => setEmails(r.data)).catch(() => setEmails([]));

  const onSelect = (l) => { setSelected(l); loadNotes(l.id); loadEmails(l.id); };

  const update = async (id, patch) => {
    const { data } = await api.patch(`/leads/${id}`, patch);
    setLeads((prev) => prev.map(l => l.id === id ? data : l));
    if (selected?.id === id) setSelected(data);
    toast.success("Updated");
  };

  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((prev) => prev.filter(l => l.id !== id));
    if (selected?.id === id) setSelected(null);
    toast.success("Deleted");
  };

  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    const { data } = await api.post(`/leads/${selected.id}/notes`, { text: noteText });
    setNotes(prev => [data, ...prev]);
    setNoteText("");
  };

  const columns = STATUSES.map((s) => ({
    status: s,
    leads: leads.filter(l => l.status === s),
  }));

  const setColPage = (status, delta) => {
    setColPages(p => ({ ...p, [status]: Math.max(0, (p[status] || 0) + delta) }));
  };

  const onDragStart = (e, lead) => {
    setDragging(lead);
    try { e.dataTransfer.effectAllowed = "move"; } catch (_) {}
  };
  const onDragOverCol = (e, status) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch (_) {}
    setDragOver(status);
  };
  const onDropCol = async (e, status) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragging || dragging.status === status) { setDragging(null); return; }
    // optimistic
    const prev = leads;
    setLeads(prev.map(l => l.id === dragging.id ? { ...l, status } : l));
    try {
      await api.patch(`/leads/${dragging.id}`, { status });
      toast.success(`Moved to ${status}`);
    } catch {
      setLeads(prev);
      toast.error("Move failed");
    }
    setDragging(null);
  };

  const toggleColSelect = (colLeads) => {
    const emailable = colLeads.filter(l => l.contact_email);
    const allInCol = emailable.every(l => selectedIds.has(l.id));
    const next = new Set(selectedIds);
    if (allInCol) emailable.forEach(l => next.delete(l.id));
    else emailable.forEach(l => next.add(l.id));
    setSelectedIds(next);
  };

  return (
    <div className="p-8 space-y-6">
      <SectionTitle eyebrow="Pipeline" title="Leads"
        actions={
          <>
            <button data-testid="btn-select-all" onClick={() => {
              if (selectedIds.size === leads.length) setSelectedIds(new Set());
              else setSelectedIds(new Set(leads.filter(l => l.contact_email).map(l => l.id)));
            }} className="border border-border px-3 py-2 text-xs rounded-sm hover:bg-accent flex items-center gap-1">
              {selectedIds.size === leads.length && leads.length > 0
                ? <CheckSquare size={14} weight="fill" /> : <Square size={14} />}
              {selectedIds.size ? `${selectedIds.size} selected` : "Select all with email"}
            </button>
            <button data-testid="btn-bulk-email" disabled={!selectedIds.size} onClick={() => setBulkOpen(true)}
                    className="bg-primary text-primary-foreground px-3 py-2 text-xs rounded-sm flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50">
              <PaperPlaneTilt size={12} /> Bulk email
            </button>
          </>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 overflow-x-auto">
          <div className="flex gap-3 min-w-max">
            {columns.map(({ status, leads: ls }) => {
              const page = colPages[status] || 0;
              const start = page * PAGE;
              const paged = ls.slice(start, start + PAGE);
              const emailable = ls.filter(l => l.contact_email);
              const allSel = emailable.length > 0 && emailable.every(l => selectedIds.has(l.id));
              const hasMore = ls.length > start + PAGE;
              return (
              <div key={status}
                   onDragOver={(e) => onDragOverCol(e, status)}
                   onDragLeave={() => setDragOver(null)}
                   onDrop={(e) => onDropCol(e, status)}
                   className={`w-64 border rounded-sm p-3 shrink-0 transition-colors ${dragOver === status ? "border-primary bg-primary/5" : "border-border bg-secondary/40"}`}
                   data-testid={`col-${status.replace(/\s/g, '-')}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`col-select-${status.replace(/\s/g, '-')}`}
                            onClick={() => toggleColSelect(ls)}
                            disabled={emailable.length === 0}
                            className="p-0.5 hover:bg-accent rounded-sm disabled:opacity-30">
                      {allSel ? <CheckSquare size={12} weight="fill" className="text-primary" /> : <Square size={12} />}
                    </button>
                    <div className="overline-label text-muted-foreground">{status}</div>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{ls.length}</span>
                </div>
                <div className="space-y-2 min-h-[80px]">
                  {paged.map((l) => {
                    const isSel = selectedIds.has(l.id);
                    const toggleSel = (e) => {
                      e.stopPropagation();
                      const next = new Set(selectedIds);
                      if (isSel) next.delete(l.id); else next.add(l.id);
                      setSelectedIds(next);
                    };
                    return (
                    <div key={l.id} onClick={() => onSelect(l)}
                         draggable
                         onDragStart={(e) => onDragStart(e, l)}
                         className={`border border-border bg-card p-3 rounded-sm cursor-move hover:border-primary transition-colors ${selected?.id === l.id ? "border-primary" : ""} ${dragging?.id === l.id ? "opacity-40" : ""}`}
                         data-testid={`lead-card-${l.id}`}>
                      <div className="flex items-center gap-2">
                        <button onClick={toggleSel} data-testid={`lead-select-${l.id}`}
                                className="p-0.5 hover:bg-accent rounded-sm">
                          {isSel ? <CheckSquare size={14} weight="fill" className="text-primary" /> : <Square size={14} />}
                        </button>
                        {l.app_icon && <img src={l.app_icon} className="w-6 h-6 rounded-sm" alt="" />}
                        <div className="text-sm font-medium truncate flex-1">{l.app_name}</div>
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono truncate mt-1">{l.package_name}</div>
                      <div className="flex items-center justify-between mt-2 flex-wrap gap-1">
                        <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm font-mono ${scoreColor(l.lead_score)}`}>{l.lead_score}</span>
                        <div className="flex items-center gap-1">
                          {l.has_replied && <span className="text-[9px] px-1 py-0.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 rounded-sm border border-emerald-500/30">↩ replied</span>}
                          {l.followup_scheduled_at && !l.followup_sent_at && !l.has_replied && (
                            <span className="text-[9px] px-1 py-0.5 bg-amber-500/15 text-amber-700 dark:text-amber-400 rounded-sm border border-amber-500/30 flex items-center gap-0.5">
                              <ArrowClockwise size={8} /> follow-up
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )})}
                  {!ls.length && <div className="text-[11px] text-muted-foreground text-center py-4 border border-dashed border-border/60 rounded-sm">Drop here</div>}
                </div>
                {(ls.length > PAGE) && (
                  <div className="flex items-center justify-between mt-2 text-[10px] text-muted-foreground">
                    <button disabled={page === 0} onClick={() => setColPage(status, -1)}
                            data-testid={`col-prev-${status.replace(/\s/g, '-')}`}
                            className="border border-border px-1.5 py-0.5 rounded-sm disabled:opacity-30 hover:bg-accent">Prev</button>
                    <span className="font-mono">{start + 1}–{Math.min(start + PAGE, ls.length)} / {ls.length}</span>
                    <button disabled={!hasMore} onClick={() => setColPage(status, +1)}
                            data-testid={`col-next-${status.replace(/\s/g, '-')}`}
                            className="border border-border px-1.5 py-0.5 rounded-sm disabled:opacity-30 hover:bg-accent">Next</button>
                  </div>
                )}
              </div>
            )})}
          </div>
        </div>

        <aside className="border border-border p-4 bg-card">
          {!selected ? (
            <div className="text-sm text-muted-foreground py-10 text-center">Select a lead to view details</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                {selected.app_icon && <img src={selected.app_icon} className="w-10 h-10 rounded-sm" alt="" />}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{selected.app_name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono truncate">{selected.package_name}</div>
                </div>
                <button data-testid={`btn-del-lead-${selected.id}`} onClick={() => remove(selected.id)}
                        className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm"><Trash size={14} /></button>
              </div>

              <div>
                <label className="overline-label text-muted-foreground block mb-1">Status</label>
                <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value })}
                        data-testid="lead-status-select"
                        className="w-full border border-input bg-background px-2 py-1.5 text-xs rounded-sm">
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="overline-label text-muted-foreground block mb-1">Owner</label>
                <input value={selected.owner || ""} onChange={(e) => setSelected({ ...selected, owner: e.target.value })}
                       onBlur={() => update(selected.id, { owner: selected.owner })}
                       className="w-full border border-input bg-background px-2 py-1.5 text-xs rounded-sm" />
              </div>
              <div>
                <label className="overline-label text-muted-foreground block mb-1">Contact Email</label>
                <input value={selected.contact_email || ""} onChange={(e) => setSelected({ ...selected, contact_email: e.target.value })}
                       onBlur={() => update(selected.id, { contact_email: selected.contact_email })}
                       className="w-full border border-input bg-background px-2 py-1.5 text-xs rounded-sm" />
              </div>
              <div>
                <label className="overline-label text-muted-foreground block mb-1">Developer Number</label>
                <input value={selected.developer_number || ""} onChange={(e) => setSelected({ ...selected, developer_number: e.target.value })}
                       onBlur={() => update(selected.id, { developer_number: selected.developer_number })}
                       data-testid="lead-dev-number"
                       placeholder="+91 90000 00000"
                       className="w-full border border-input bg-background px-2 py-1.5 text-xs rounded-sm" />
              </div>
              <div>
                <label className="overline-label text-muted-foreground block mb-1">Next Follow-up</label>
                <input type="date" value={selected.next_followup?.slice(0, 10) || ""}
                       onChange={(e) => update(selected.id, { next_followup: e.target.value })}
                       className="w-full border border-input bg-background px-2 py-1.5 text-xs rounded-sm" />
              </div>

              <div>
                <label className="overline-label text-muted-foreground block mb-1">Follow-up (auto, days)</label>
                <input type="number" min={0} max={30}
                       value={selected.followup_days || ""}
                       onChange={(e) => setSelected({ ...selected, followup_days: e.target.value ? Number(e.target.value) : 0 })}
                       onBlur={() => update(selected.id, { followup_days: selected.followup_days || 0 })}
                       data-testid="lead-followup-days"
                       placeholder="e.g. 4"
                       className="w-full border border-input bg-background px-2 py-1.5 text-xs rounded-sm" />
                {selected.followup_scheduled_at && !selected.followup_sent_at && !selected.has_replied && (
                  <div className="text-[10px] text-amber-600 mt-1">↻ Follow-up scheduled: {formatDate(selected.followup_scheduled_at)}</div>
                )}
                {selected.followup_sent_at && (
                  <div className="text-[10px] text-emerald-600 mt-1">✓ Follow-up sent {formatDate(selected.followup_sent_at)}</div>
                )}
                {selected.has_replied && <div className="text-[10px] text-emerald-600 mt-1">↩ Reply received</div>}
              </div>

              <button data-testid="btn-open-email" onClick={() => setEmailOpen(true)}
                      className="w-full bg-primary text-primary-foreground text-xs font-medium py-2 rounded-sm flex items-center justify-center gap-1.5 hover:opacity-90">
                <PaperPlaneTilt size={12} /> Send outreach email
              </button>

              {emails.length > 0 && (
                <div className="border-t border-border pt-3">
                  <div className="overline-label text-muted-foreground mb-2">Email History ({emails.length})</div>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {emails.map((e) => (
                      <div key={e.id} className="border border-border/60 p-2 rounded-sm">
                        <div className="text-xs font-medium truncate">{e.subject}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">→ {e.to} · {formatDate(e.created_at)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-border pt-3">
                <div className="overline-label text-muted-foreground mb-2 flex items-center gap-1"><ChatCircle size={12} /> Notes</div>
                <div className="flex gap-1 mb-2">
                  <input value={noteText} onChange={(e) => setNoteText(e.target.value)}
                         data-testid="note-input"
                         placeholder="Add a note…"
                         className="flex-1 border border-input bg-background px-2 py-1.5 text-xs rounded-sm" />
                  <button onClick={addNote} data-testid="btn-add-note"
                          className="bg-primary text-primary-foreground px-2 rounded-sm"><Plus size={14} /></button>
                </div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {notes.map((n) => (
                    <div key={n.id} className="border border-border/60 p-2 rounded-sm">
                      <div className="text-xs">{n.text}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{n.author} · {formatDate(n.created_at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
      {emailOpen && selected && (
        <EmailComposer lead={selected} onClose={() => setEmailOpen(false)} onSent={() => { load(); loadEmails(selected.id); }} />
      )}
      {bulkOpen && (
        <BulkEmailComposer leadIds={Array.from(selectedIds)}
                           onClose={() => { setBulkOpen(false); setSelectedIds(new Set()); load(); }} />
      )}
    </div>
  );
}
